import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase/client'

interface AuthState {
  user: User | null
  session: Session | null
  isLoading: boolean
  isLoginModalOpen: boolean
  setSession: (session: Session | null) => void
  openLoginModal: () => void
  closeLoginModal: () => void
  signOut: () => Promise<void>
  // Call once at app mount — subscribes to Supabase auth events and returns the unsubscribe fn
  initialize: () => () => void
}

export const useAuthStore = create<AuthState>()(
  devtools(
    (set) => ({
      user: null,
      session: null,
      isLoading: true,
      isLoginModalOpen: false,

      setSession: (session) =>
        set({ session, user: session?.user ?? null, isLoading: false }),

      openLoginModal: () => set({ isLoginModalOpen: true }),
      closeLoginModal: () => set({ isLoginModalOpen: false }),

      signOut: async () => {
        await supabase.auth.signOut()
        set({ user: null, session: null })
      },

      initialize: () => {
        const {
          data: { subscription },
        } = supabase.auth.onAuthStateChange((_event, session) => {
          set({ session, user: session?.user ?? null, isLoading: false })
        })
        return () => subscription.unsubscribe()
      },
    }),
    { name: 'auth-store' }
  )
)
